import React, { useState, useEffect, useRef } from "react";
import api from "../api";
import toast from "react-hot-toast";
import { format } from "date-fns";

const OUTCOMES = [
  { value: "interested",     label: "Interested",     color: "#2f9e44", bg: "#ebfbee", border: "#b2f2bb" },
  { value: "not_interested", label: "Not Interested", color: "#c92a2a", bg: "#fff5f5", border: "#ffc9c9" },
  { value: "call_later",     label: "Call Later",     color: "#6741d9", bg: "#f3f0ff", border: "#d0bfff" },
  { value: "wrong_number",   label: "Wrong Number",   color: "#868e96", bg: "#f8f9fa", border: "#dee2e6" },
  { value: "schedule_demo",  label: "Schedule Demo",  color: "#1971c2", bg: "#e7f5ff", border: "#a5d8ff" },
];

function toLocal(d) {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function getShortcuts() {
  const now = new Date();
  const add = (h) => { const d = new Date(now); d.setHours(d.getHours() + h); return d; };
  const atTime = (h, m, nextDay) => {
    const d = new Date(now);
    if (nextDay) d.setDate(d.getDate() + 1);
    d.setHours(h, m, 0, 0);
    return d;
  };
  const afternoon = atTime(14,0,false).getTime() > now.getTime() ? atTime(14,0,false) : atTime(14,0,true);
  const evening   = atTime(18,0,false).getTime() > now.getTime() ? atTime(18,0,false) : atTime(18,0,true);
  return [
    { label: "1 hour",    value: toLocal(add(1)) },
    { label: "3 hours",   value: toLocal(add(3)) },
    { label: "5 hours",   value: toLocal(add(5)) },
    { label: "Tomorrow",  value: toLocal(atTime(10,0,true)) },
    { label: "Afternoon", value: toLocal(afternoon) },
    { label: "Evening",   value: toLocal(evening) },
  ];
}

function fmtSec(s) {
  return String(Math.floor(s/60)).padStart(2,"0") + ":" + String(s%60).padStart(2,"0");
}

function fmtDur(secs) {
  if (!secs) return "--";
  const m = Math.floor(secs/60), s = secs%60;
  return m > 0 ? m + "m " + s + "s" : s + "s";
}

const STATUS_COLORS = {
  interested: "#2f9e44", not_interested: "#c92a2a", call_later: "#6741d9",
  wrong_number: "#868e96", schedule_demo: "#1971c2", pending: "#8b93a7"
};

export default function CallingDesk() {
  const [lead, setLead] = useState(null);
  const [phase, setPhase] = useState("ready");
  const [elapsed, setElapsed] = useState(0);
  const [outcome, setOutcome] = useState({ status:"", notes:"", scheduledDemoAt:"", demoDetails:"", followUpAt:"" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showDNC, setShowDNC] = useState(false);
  const [dncReason, setDncReason] = useState("");
  const timerRef = useRef(null);

  const fetchNext = async () => {
    setLoading(true);
    clearInterval(timerRef.current);
    setShowHistory(false);
    setShowDNC(false);
    try {
      const { data } = await api.get("/leads/next");
      setLead(data);
      setPhase("ready");
      setElapsed(0);
      setOutcome({ status:"", notes:"", scheduledDemoAt:"", demoDetails:"", followUpAt:"" });
      setHistory([]);
    } catch (e) { toast.error("Failed to load lead"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchNext(); return () => clearInterval(timerRef.current); }, []);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/leads/history/" + lead._id);
      setHistory(data);
      setShowHistory(true);
    } catch (e) { toast.error("Failed to load history"); }
  };

  const markDNC = async () => {
    try {
      await api.put("/leads/dnc/" + lead._id, { reason: dncReason });
      toast.success("Marked as Do Not Call");
      setShowDNC(false);
      setDncReason("");
      await fetchNext();
    } catch (e) { toast.error("Failed"); }
  };

  const startCall = async () => {
    try {
      await api.post("/leads/start/" + lead._id);
      setPhase("calling");
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(e => e+1), 1000);
      window.location.href = "tel:" + lead.number;
    } catch (e) { toast.error("Failed"); }
  };

  const endCall = () => { clearInterval(timerRef.current); setPhase("outcome"); };

  const submitOutcome = async () => {
    if (!outcome.status) return toast.error("Select an outcome");
    if (outcome.status === "call_later" && !outcome.followUpAt) return toast.error("Select a follow-up time");
    setSaving(true);
    try {
      await api.put("/leads/stop/" + lead._id, outcome);
      if (outcome.status === "call_later" && outcome.followUpAt) {
        await api.post("/followups", { leadId: lead._id, scheduledAt: outcome.followUpAt, notes: outcome.notes });
      }
      toast.success("Saved");
      await fetchNext();
    } catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"60vh" }}>
      <div style={{ width:18, height:18, border:"2px solid #e2e5ef", borderTopColor:"#3b5bdb", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );

  if (!lead) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"60vh" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:14, fontWeight:600, color:"#0f1117", marginBottom:6 }}>All leads completed</div>
        <div style={{ fontSize:13, color:"#8b93a7", marginBottom:16 }}>No more leads available right now.</div>
        <button className="btn btn-secondary btn-sm" onClick={fetchNext}>Refresh</button>
      </div>
    </div>
  );

  const shortcuts = getShortcuts();
  const topColor = phase==="calling" ? "#2f9e44" : phase==="outcome" ? "#3b5bdb" : "#e2e5ef";
  const isFollowUp = lead._followUp === true;

  return (
    <div style={{ maxWidth:680, margin:"0 auto" }}>
      <style>{"@keyframes spin{to{transform:rotate(360deg)}} @keyframes blink{0%,100%{opacity:1}50%{opacity:0.35}}"}</style>

      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:17, fontWeight:700, color:"#0f1117" }}>Calling Desk</div>
        <div style={{ fontSize:12.5, color:"#8b93a7", marginTop:2 }}>
          {phase==="ready" ? "Review and start the call" : phase==="calling" ? "Call in progress" : "Log the outcome"}
        </div>
      </div>

      {/* Follow-up badge */}
      {isFollowUp && (
        <div style={{ background:"#f3f0ff", border:"1px solid #d0bfff", borderRadius:8, padding:"9px 14px", marginBottom:12, fontSize:13, color:"#6741d9", fontWeight:500 }}>
          Follow-up due — this lead was scheduled for a callback
        </div>
      )}

      {/* Lead card */}
      <div style={{ background:"#fff", border:"1px solid #e2e5ef", borderRadius:12, overflow:"hidden", marginBottom:12 }}>
        <div style={{ height:3, background:topColor, transition:"background 0.2s" }} />

        <div style={{ padding:"18px 22px", borderBottom:"1px solid #f1f3f9" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ width:44, height:44, borderRadius:10, background:"#eef2ff", color:"#3b5bdb", display:"flex", alignItems:"center", justifyContent:"center", fontSize:17, fontWeight:700, border:"1px solid #c5d0fa", flexShrink:0 }}>
                {lead.name?.[0]?.toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize:16, fontWeight:700, color:"#0f1117" }}>{lead.name}</div>
                <div style={{ fontSize:12.5, color:"#8b93a7", marginTop:2 }}>{lead.city || "Unknown location"}</div>
              </div>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              {phase==="calling" && (
                <div style={{ display:"flex", alignItems:"center", gap:7, background:"#ebfbee", border:"1px solid #b2f2bb", borderRadius:8, padding:"6px 12px" }}>
                  <span style={{ width:7, height:7, borderRadius:"50%", background:"#2f9e44", display:"inline-block", animation:"blink 1.2s infinite" }} />
                  <span style={{ fontWeight:700, fontSize:15, color:"#2f9e44", fontVariantNumeric:"tabular-nums", letterSpacing:"1px" }}>{fmtSec(elapsed)}</span>
                </div>
              )}
              {phase==="outcome" && elapsed>0 && (
                <span style={{ fontSize:12.5, color:"#8b93a7" }}>Duration: <strong style={{ color:"#4b5263" }}>{fmtSec(elapsed)}</strong></span>
              )}
              {/* Action buttons top-right */}
              <button onClick={loadHistory}
                style={{ padding:"5px 10px", background:"#f7f8fc", border:"1px solid #e2e5ef", borderRadius:6, fontSize:12, color:"#4b5263", cursor:"pointer", fontFamily:"inherit" }}>
                History
              </button>
              <button onClick={() => setShowDNC(true)}
                style={{ padding:"5px 10px", background:"#fff5f5", border:"1px solid #ffc9c9", borderRadius:6, fontSize:12, color:"#c92a2a", cursor:"pointer", fontFamily:"inherit" }}>
                DNC
              </button>
            </div>
          </div>
        </div>

        <div style={{ padding:"14px 22px 18px" }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:18 }}>
            {[{label:"Phone",value:lead.number},{label:"City",value:lead.city||"--"},{label:"Batch",value:lead.batchName||"--"},{label:"Comments",value:lead.comments||"--"}].map(f => (
              <div key={f.label} style={{ background:"#f7f8fc", borderRadius:8, padding:"9px 11px", border:"1px solid #eef0f6" }}>
                <div style={{ fontSize:10.5, color:"#8b93a7", textTransform:"uppercase", letterSpacing:"0.06em", fontWeight:600, marginBottom:3 }}>{f.label}</div>
                <div style={{ fontSize:13, fontWeight:600, color:"#0f1117", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.value}</div>
              </div>
            ))}
          </div>

          {phase==="ready" && (
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={startCall} style={{ flex:1, padding:"10px 0", background:"#2f9e44", color:"#fff", border:"none", borderRadius:8, fontSize:14, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                Call {lead.number}
              </button>
              <button onClick={fetchNext} style={{ padding:"10px 18px", background:"#fff", color:"#4b5263", border:"1px solid #e2e5ef", borderRadius:8, fontSize:14, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>
                Skip
              </button>
            </div>
          )}
          {phase==="calling" && (
            <button onClick={endCall} style={{ width:"100%", padding:"10px 0", background:"#c92a2a", color:"#fff", border:"none", borderRadius:8, fontSize:14, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
              End Call
            </button>
          )}
          {phase==="outcome" && (
            <div style={{ padding:"9px 13px", background:"#ebfbee", border:"1px solid #b2f2bb", borderRadius:8, fontSize:13, color:"#2f9e44", fontWeight:500 }}>
              Call ended - select an outcome below
            </div>
          )}
        </div>
      </div>

      {/* Outcome panel */}
      {phase==="outcome" && (
        <div style={{ background:"#fff", border:"1px solid #e2e5ef", borderRadius:12, padding:"18px 22px" }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#8b93a7", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:12 }}>Call Outcome</div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:8, marginBottom:16 }}>
            {OUTCOMES.map(o => {
              const sel = outcome.status===o.value;
              return (
                <button key={o.value} onClick={() => setOutcome(x => ({...x, status:o.value}))}
                  style={{ padding:"10px 6px", borderRadius:8, cursor:"pointer", textAlign:"center", border:"1.5px solid "+(sel?o.color:"#e2e5ef"), background:sel?o.bg:"#fff", color:sel?o.color:"#4b5263", fontWeight:sel?600:400, fontSize:12.5, fontFamily:"inherit", transition:"all 0.1s" }}>
                  {o.label}
                </button>
              );
            })}
          </div>

          {outcome.status==="call_later" && (
            <div style={{ background:"#f3f0ff", border:"1px solid #d0bfff", borderRadius:8, padding:"13px 15px", marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:600, color:"#6741d9", marginBottom:10 }}>Schedule Follow-up</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10 }}>
                {shortcuts.map(s => (
                  <button key={s.label} onClick={() => setOutcome(o => ({...o, followUpAt:s.value}))}
                    style={{ padding:"5px 11px", borderRadius:6, cursor:"pointer", fontSize:12, fontWeight:500, fontFamily:"inherit", transition:"all 0.1s", border:"1px solid "+(outcome.followUpAt===s.value?"#6741d9":"#d0bfff"), background:outcome.followUpAt===s.value?"#6741d9":"#fff", color:outcome.followUpAt===s.value?"#fff":"#6741d9" }}>
                    {s.label}
                  </button>
                ))}
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label>Custom date and time</label>
                <input type="datetime-local" value={outcome.followUpAt} onChange={e => setOutcome(o => ({...o, followUpAt:e.target.value}))} />
              </div>
            </div>
          )}

          {outcome.status==="schedule_demo" && (
            <div style={{ background:"#e7f5ff", border:"1px solid #a5d8ff", borderRadius:8, padding:"13px 15px", marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:600, color:"#1971c2", marginBottom:10 }}>Schedule Demo</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div className="form-group" style={{ marginBottom:0 }}>
                  <label>Date and Time</label>
                  <input type="datetime-local" value={outcome.scheduledDemoAt} onChange={e => setOutcome(o => ({...o, scheduledDemoAt:e.target.value}))} />
                </div>
                <div className="form-group" style={{ marginBottom:0 }}>
                  <label>Topic</label>
                  <input value={outcome.demoDetails} onChange={e => setOutcome(o => ({...o, demoDetails:e.target.value}))} placeholder="What to cover" />
                </div>
              </div>
            </div>
          )}

          <div className="form-group" style={{ marginBottom:14 }}>
            <label>Notes</label>
            <textarea rows={3} value={outcome.notes} onChange={e => setOutcome(o => ({...o, notes:e.target.value}))} placeholder="Conversation summary..." style={{ resize:"vertical" }} />
          </div>

          <button onClick={submitOutcome} disabled={saving||!outcome.status}
            style={{ width:"100%", padding:"10px 0", background:outcome.status?"#3b5bdb":"#e2e5ef", color:outcome.status?"#fff":"#8b93a7", border:"none", borderRadius:8, fontSize:14, fontWeight:600, cursor:outcome.status?"pointer":"not-allowed", fontFamily:"inherit" }}>
            {saving ? "Saving..." : "Save and Next Lead"}
          </button>
        </div>
      )}

      {/* History modal */}
      {showHistory && (
        <div className="modal-overlay" onClick={() => setShowHistory(false)}>
          <div className="modal" style={{ maxWidth:520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <h2>Call History — {lead.name}</h2>
              <button className="modal-close" onClick={() => setShowHistory(false)}>x</button>
            </div>
            <div className="modal-body">
              {history.length === 0 ? (
                <div style={{ textAlign:"center", color:"#8b93a7", padding:"24px 0", fontSize:13 }}>No previous calls for this lead</div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {history.map(h => (
                    <div key={h._id} style={{ background:"#f7f8fc", borderRadius:8, padding:"12px 14px", border:"1px solid #eef0f6" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <span style={{ fontSize:12, fontWeight:600, color: STATUS_COLORS[h.status] || "#8b93a7", background:"#fff", border:"1px solid #e2e5ef", borderRadius:4, padding:"2px 8px" }}>
                            {h.status ? h.status.replace(/_/g," ") : "pending"}
                          </span>
                          <span style={{ fontSize:12, color:"#8b93a7" }}>by {h.calledBy?.name || "Unknown"}</span>
                        </div>
                        <div style={{ fontSize:11.5, color:"#8b93a7" }}>
                          {h.createdAt ? format(new Date(h.createdAt), "MMM d, yyyy h:mm a") : "--"}
                          {h.duration > 0 && <span style={{ marginLeft:8 }}>{fmtDur(h.duration)}</span>}
                        </div>
                      </div>
                      {h.notes && <div style={{ fontSize:12.5, color:"#4b5263", lineHeight:1.5 }}>{h.notes}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DNC modal */}
      {showDNC && (
        <div className="modal-overlay" onClick={() => setShowDNC(false)}>
          <div className="modal" style={{ maxWidth:400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <h2>Mark as Do Not Call</h2>
              <button className="modal-close" onClick={() => setShowDNC(false)}>x</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize:13, color:"#4b5263", marginBottom:14 }}>
                This will permanently remove <strong>{lead.name}</strong> ({lead.number}) from all calling queues.
              </p>
              <div className="form-group">
                <label>Reason (optional)</label>
                <input value={dncReason} onChange={e => setDncReason(e.target.value)} placeholder="e.g. Customer requested no calls" />
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button className="btn btn-danger" onClick={markDNC} style={{ flex:1 }}>Mark as DNC</button>
                <button className="btn btn-secondary" onClick={() => setShowDNC(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}